import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, message, Typography, Checkbox, Radio, Popconfirm } from 'antd';
import { createWebhook, editWebhook } from '@api/notificationChannel';
import {
  StyledWrapper,
  StyledInlineElements,
  StyledAuthFields,
  CancelButtonWrapper,
  FooterWrapper,
  StyledBackButton,
} from './styled';
import EmailInput from './FormElement/EmailList';
import { AuthType } from '../constant';
import {
  getAlertStandardData,
  getAlertCustomData,
  getMAOpsData,
  filterEditedAlertStandardFields,
  filterCheckedEditedMAOptFields,
} from '../utils';
import { productMap } from '@src/utils/aiAlerts/mapping';
import { CURRENT_PRODUCT_MODE, isAppModule } from '@src/utils/constants';
import AlertFieldsForm from './AlertFieldsForm';
import { useSelector } from 'react-redux';
import { isAdminRoleSelector } from '@src/store/common/selectors';
import { AlertType } from '../types';

const { Title } = Typography;
const baseUrl = window.conviva.baseUrl;

const WebhooksForm = ({ webhook }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const context = window.conviva.context;
  const isAdminUser = useSelector(isAdminRoleSelector);

  const [verifiedWebhookStatusAI, setVerifiedWebhookStatusAI] = useState(false);
  const [verifiedWebhookStatusMA, setVerifiedWebhookStatusMA] = useState(false);
  const [cancelPopOpenStatus, setCancelPopOpenStatus] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);

  const [showAuthOptions, setShowAuthOptions] = useState(false);
  const [authType, setAuthType] = useState(AuthType.credential);
  const [editMode, setEditMode] = useState(false);
  const [form] = Form.useForm();
  const nameWatch = Form.useWatch('name', form);
  const emailWatch = Form.useWatch('emails', form);
  const alertAITypeWatch = Form.useWatch('alertAIType', form);
  const alertManualTypeWatch = Form.useWatch('alertManualType', form);

  useEffect(() => {
    if (isAppModule) {
      form.setFieldsValue({
        alertAIType: true,
        alertManualType: false,
      });
    }
    if (Object.keys(webhook).length) {
      setEditMode(true);
      form.setFieldsValue({
        name: webhook.name,
        emails: webhook.emails,
        url: webhook.webhookUrl,
      });

      const webhookAlertType: AlertType = webhook.alertType;
      if (webhookAlertType) {
        if (webhookAlertType === 'ALL') {
          form.setFieldsValue({
            alertAIType: true,
            alertManualType: true,
          });
        } else if (webhookAlertType === 'AI') {
          form.setFieldsValue({
            alertAIType: true,
            alertManualType: false,
          });
        } else if (webhookAlertType === 'Manual') {
          form.setFieldsValue({ alertManualType: true, alertAIType: false });
        } else form.setFieldsValue({ alertManualType: false, alertAIType: false });
      }
      if (webhook.authentication) {
        setShowAuthOptions(true);
        if (webhook.authentication.type === AuthType.credential) {
          setAuthType(AuthType.credential);
          form.setFieldsValue({
            auth: true,
            username: Buffer.from(webhook.authentication.username, 'base64').toString('utf-8'),
            password: Buffer.from(webhook.authentication.tokenOrPassword, 'base64').toString(
              'utf-8',
            ),
          });
        } else if (webhook.authentication.type === AuthType.token) {
          setAuthType(AuthType.token);
          form.setFieldsValue({
            auth: true,
            token: Buffer.from(webhook.authentication.tokenOrPassword, 'base64').toString('utf-8'),
          });
        } else {
          setAuthType(AuthType.custom);
          form.setFieldsValue({
            auth: true,
            headerKey: webhook.authentication.headerKey,
            token: Buffer.from(webhook.authentication.tokenOrPassword, 'base64').toString('utf-8'), // check if encoding works??
          });
        }
      }
      if (webhook.config.aiConfig) {
        const alertAIFields = webhook.config.aiConfig.alertsFields;
        const alertsFieldsRefactored = filterEditedAlertStandardFields(alertAIFields, 'AI');
        const customAIFields = webhook.config.aiConfig.customFields;
        const customFieldsRefactored = Object.keys(customAIFields).map((key) => {
          return {
            customKey: key,
            customValue: customAIFields[key],
          };
        });
        form.setFieldsValue({
          alertAIFields: alertsFieldsRefactored,
          customAIFields: customFieldsRefactored,
        });
      }
      if (webhook.config.maConfig) {
        const alertManualFields = webhook.config.maConfig.alertsFields;
        const alertsFieldsRefactored = filterEditedAlertStandardFields(alertManualFields, 'Manual');
        const customManualFields = webhook.config.maConfig.customFields;
        const customFieldsRefactored = Object.keys(customManualFields).map((key) => {
          return {
            customKey: key,
            customValue: customManualFields[key],
          };
        });
        const { alertManualOpsFields, checkedAlertManualOptFields } =
          filterCheckedEditedMAOptFields(webhook.config.maConfig.maOpsFields);
        form.setFieldsValue({
          alertManualFields: alertsFieldsRefactored,
          customManualFields: customFieldsRefactored,
          alertManualOpsFields,
          checkedAlertManualOptFields,
        });
      }
    } else {
      setEditMode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhook, editMode, isAppModule]);

  const createWebhooksMutation = useMutation(createWebhook, {
    onSuccess: () => {
      queryClient.invalidateQueries(['webhooksListData']);
    },
  });

  const editWebhooksMutation = useMutation(editWebhook, {
    onSuccess: () => {
      queryClient.invalidateQueries(['webhooksListData']);
    },
  });

  const onFinish = async () => {
    message.success('Submit success!');
    setIsFormChanged(false);
    try {
      const values = await form.validateFields([
        'name',
        'url',
        'emails',
        'auth',
        'alertAIType',
        'alertManualType',
        'alertAIFields',
        'alertManualFields',
        'alertManualOpsFields',
        'checkedAlertManualOptFields',
        'customAIFields',
        'customManualFields',
      ]);
      let authValues;
      if (values.auth) {
        if (authType === AuthType.credential) {
          const authFormData = await form.validateFields(['username', 'password']);
          authValues = {
            type: AuthType.credential,
            username: Buffer.from(authFormData.username).toString('base64'),
            tokenOrPassword: Buffer.from(authFormData.password).toString('base64'),
          };
        } else if (authType === AuthType.token) {
          const authFormData = await form.validateFields(['token']);
          authValues = {
            type: AuthType.token,
            tokenOrPassword: Buffer.from(authFormData.token).toString('base64'),
          };
        } else {
          const authFormData = await form.validateFields(['token', 'headerKey']);
          authValues = {
            type: AuthType.custom,
            headerKey: authFormData.headerKey,
            tokenOrPassword: Buffer.from(authFormData.token).toString('base64'),
          };
        }
      } else {
        authValues = false;
      }
      const alertAIFieldsRefactored = getAlertStandardData(values.alertAIFields, 'AI');
      const customFieldsRefactored = getAlertCustomData(values.customAIFields || []);
      const alertMAFieldsRefactored = getAlertStandardData(values.alertManualFields, 'Manual');
      const opsMAFieldsRefactored = getMAOpsData(
        values.alertManualOpsFields,
        values.checkedAlertManualOptFields,
      );
      const customMAFieldsRefactored = getAlertCustomData(values.customManualFields || []);
      const alertType: AlertType =
        !!values.alertAIType && !!values.alertManualType
          ? 'ALL'
          : values.alertAIType
            ? 'AI'
            : 'Manual';
      const payload = {
        name: values.name,
        webhookUrl: values.url,
        emails: values.emails,
        alertType: alertType,
        context,
        product: productMap[CURRENT_PRODUCT_MODE],
        authentication: authValues,
        config: {
          aiConfig: {
            alertFields: alertAIFieldsRefactored,
            customFields: customFieldsRefactored,
          },
          maConfig: {
            alertFields: alertMAFieldsRefactored,
            maOpsFields: opsMAFieldsRefactored,
            customFields: customMAFieldsRefactored,
          },
        },
      };
      if (editMode) {
        await editWebhooksMutation.mutateAsync({
          payload: { ...payload, webhookUrl: decodeURI(values.url) },
          id: webhook.webhookId,
        });
      } else {
        await createWebhooksMutation.mutateAsync(payload);
      }
      navigate(`${baseUrl}/settings/webhooks`);
    } catch (error) {
      console.error('Validation error:', error);
    }
  };

  const handleAuthTypeChange = async (value) => {
    setAuthType(value);
    if (value === AuthType.credential) {
      await form.resetFields(['token']);
    } else {
      await form.resetFields(['username', 'password']);
    }
    setIsFormChanged(true);
  };

  const validateFormFields = async ({ alertFieldsKey, customFieldsKey, opsAlertFieldsKeys }) => {
    try {
      const values = await form.validateFields([
        'url',
        'auth',
        alertFieldsKey,
        customFieldsKey,
        ...opsAlertFieldsKeys,
      ]);
      let authValues;
      if (values.auth) {
        if (authType === AuthType.credential) {
          const authFormData = await form.validateFields(['username', 'password']);
          authValues = {
            type: AuthType.credential,
            username: Buffer.from(authFormData.username).toString('base64'),
            tokenOrPassword: Buffer.from(authFormData.password).toString('base64'),
          };
        } else if (authType === AuthType.token) {
          const authFormData = await form.validateFields(['token']);
          authValues = {
            type: AuthType.token,
            tokenOrPassword: Buffer.from(authFormData.token).toString('base64'),
          };
        } else {
          const authFormData = await form.validateFields(['token', 'headerKey']);
          authValues = {
            type: AuthType.custom,
            headerKey: authFormData.headerKey,
            tokenOrPassword: Buffer.from(authFormData.token).toString('base64'),
          };
        }
      } else {
        authValues = false;
      }
      return { values: values, authValues: authValues };
    } catch (error) {
      console.error('Validation error:', error);
      return;
    }
  };

  const onFinishFailed = () => {
    message.error('Submit failed!');
  };

  const handleFormChange = () => {
    setIsFormChanged(true);
  };

  const layout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 20 },
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setCancelPopOpenStatus(newOpen);
      return;
    }
    if (!isFormChanged) {
      handleConfirmPopConfirm();
    } else {
      setCancelPopOpenStatus(newOpen);
    }
  };

  const handleConfirmPopConfirm = () => {
    navigate(`${baseUrl}/settings/webhooks`);
  };

  const handleCancelPopConfirm = () => {
    setCancelPopOpenStatus(false);
  };

  return (
    <StyledWrapper>
      <Title level={3}> Webhook Configuration</Title>
      <Form
        {...layout}
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        disabled={!isAdminUser}
        autoComplete="off"
        onValuesChange={handleFormChange}
      >
        <Form.Item
          name="name"
          label="Webhook Name"
          rules={[
            { required: true },
            {
              pattern: /^[a-zA-Z0-9][a-zA-Z0-9\s\W]*/,
              message: 'Invalid Input. Start with alphanumeric character.',
            },
            {
              min: 3,
              max: 256,
              message: '3 to 256 characters. Start with alphanumeric character.',
            },
          ]}
        >
          <Input placeholder="3 to 256 characters. Start with alphanumeric character." />
        </Form.Item>
        <Form.Item
          label="Emails"
          name="emails"
          rules={[
            {
              required: true,
              message: 'Please enter at least one email!',
            },
          ]}
        >
          <EmailInput emails={webhook.emails} />
        </Form.Item>
        <Form.Item
          name="url"
          label="URL"
          rules={[{ required: true }, { type: 'url', message: 'Invalid URL' }]}
        >
          <Input placeholder="Webhook URL" />
        </Form.Item>
        <StyledAuthFields>
          <Form.Item className="auth-checkbox-form-item" name="auth" valuePropName="checked">
            <Checkbox onChange={() => setShowAuthOptions(!showAuthOptions)}>
              URL has Authentication{' '}
              <span>(username & password / authorization token / custom authorization)</span>
            </Checkbox>
          </Form.Item>
          {showAuthOptions ? (
            <Radio.Group onChange={(e) => handleAuthTypeChange(e.target.value)} value={authType}>
              <StyledInlineElements>
                <Radio value={AuthType.credential}></Radio>
                <Form.Item
                  name="username"
                  rules={
                    authType === AuthType.credential
                      ? [
                          { required: true, message: 'Please add username!' },
                          { max: 256, message: 'Maximum 256 characters.' },
                          { min: 3, message: 'Minimum 3 characters.' },
                        ]
                      : []
                  }
                >
                  <Input
                    prefix={<UserOutlined className="site-form-item-icon" />}
                    placeholder="Username"
                    disabled={authType !== AuthType.credential}
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={
                    authType === AuthType.credential
                      ? [
                          { required: true, message: 'Please add password!' },
                          { max: 256, message: 'Maximum 256 characters.' },
                        ]
                      : []
                  }
                >
                  <Input
                    prefix={<LockOutlined className="site-form-item-icon" />}
                    type="password"
                    placeholder="Password"
                    disabled={authType !== AuthType.credential}
                  />
                </Form.Item>
              </StyledInlineElements>
              <StyledInlineElements>
                <Radio value={AuthType.token}></Radio>
                <Form.Item
                  name="token"
                  className="auth-token-form-item"
                  rules={
                    authType === AuthType.token
                      ? [{ required: true }, { max: 256, message: 'Maximum 256 characters.' }]
                      : []
                  }
                >
                  <Input placeholder="Token" disabled={authType !== AuthType.token} />
                </Form.Item>
              </StyledInlineElements>
              <StyledInlineElements>
                <Radio value={AuthType.custom}></Radio>
                <Form.Item
                  name="headerKey"
                  rules={
                    authType === AuthType.custom
                      ? [{ required: true }, { max: 256, message: 'Maximum 256 characters.' }]
                      : []
                  }
                >
                  <Input placeholder="Header Key" disabled={authType !== AuthType.custom} />
                </Form.Item>
                <Form.Item
                  name="token"
                  rules={
                    authType === AuthType.token
                      ? [{ required: true }, { max: 256, message: 'Maximum 256 characters.' }]
                      : []
                  }
                >
                  <Input placeholder="Header Value" disabled={authType !== AuthType.custom} />
                </Form.Item>
              </StyledInlineElements>
            </Radio.Group>
          ) : null}
        </StyledAuthFields>
        <AlertFieldsForm
          key={'ai-form'}
          formAlertType={'AI' as AlertType}
          validateFormFields={validateFormFields}
          setVerifiedWebhookStatus={setVerifiedWebhookStatusAI}
          form={form}
          isFormChanged={isFormChanged}
          webhookId={webhook.webhookId}
        />

        {!isAppModule ? (
          <div style={{ paddingTop: '20px' }}>
            <AlertFieldsForm
              key={'manual-form'}
              formAlertType={'Manual' as AlertType}
              validateFormFields={validateFormFields}
              setVerifiedWebhookStatus={setVerifiedWebhookStatusMA}
              form={form}
              isFormChanged={isFormChanged}
              webhookId={webhook.webhookId}
            />
          </div>
        ) : null}

        <FooterWrapper>
          <CancelButtonWrapper>
            {isAdminUser ? (
              <Popconfirm
                title="Are you sure? Unsaved changes will be lost."
                okText="Yes"
                open={cancelPopOpenStatus}
                cancelText="No"
                onConfirm={handleConfirmPopConfirm}
                onCancel={handleCancelPopConfirm}
                onOpenChange={handleOpenChange}
              >
                <Button>Cancel</Button>
              </Popconfirm>
            ) : (
              <StyledBackButton onClick={handleConfirmPopConfirm}>Back</StyledBackButton>
            )}
          </CancelButtonWrapper>
          <Form.Item style={{ paddingTop: '30px' }}>
            <Button
              type="primary"
              htmlType="submit"
              disabled={
                !(
                  nameWatch &&
                  emailWatch &&
                  ((alertManualTypeWatch && verifiedWebhookStatusMA) ||
                    (alertAITypeWatch && verifiedWebhookStatusAI))
                )
              }
              loading={createWebhooksMutation.isLoading || editWebhooksMutation.isLoading}
            >
              Save
            </Button>
          </Form.Item>
        </FooterWrapper>
      </Form>
    </StyledWrapper>
  );
};

export default WebhooksForm;
